/** @format */

import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  inject,
  Input,
  PLATFORM_ID,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  ViewRef,
} from '@angular/core';

import { DEVICE_CHANNELS } from '@shared/data-access/constants';
import { Environment } from '@shared/data-access/interfaces';

@Directive({
  standalone: true,
  selector: '[commonBreakPointHelper]',
})
export class BreakPointHelperDirective implements AfterViewInit {
  private environment = inject(Environment);
  // Get screenType as 'mobile' or 'desktop'
  // E.g. *commonBreakPointHelper="'mobile'"
  @Input() set commonBreakPointHelper(screenType: string) {
    this.screenType = screenType;
  }

  // Get breakpoint for component visibility in the form of letters (m, l, xl)
  // E.g. *commonBreakPointHelper="'mobile'; breakPoint: 'm'"
  // Default desktop breakpoint Large === 1024px
  @Input() set commonBreakPointHelperBreakPoint(breakPoint: string) {
    this.breakPoint = breakPoint;
  }

  ß;
  private breakPoint = 'lg';

  private screenType: string = DEVICE_CHANNELS.MOBILE; // Default screen type to mobile

  private prevViewRef: ViewRef; // Used during testing to determine component visibility

  protected readonly platformId = inject(PLATFORM_ID);
  private readonly viewRef = inject(ViewContainerRef);
  private readonly templateRef = inject(TemplateRef);
  private readonly renderer = inject(Renderer2);
  private readonly cdRef = inject(ChangeDetectorRef);

  ngAfterViewInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      const embeddedView = this.viewRef.createEmbeddedView(this.templateRef);
      const ele = embeddedView.rootNodes[0];

      if (this.screenType === DEVICE_CHANNELS.MOBILE) {
        // If screen type is mobile then show component until width has met breakpoint or greater
        this.renderer.addClass(ele, `hmf-display-${this.breakPoint.toLowerCase()}-none`);
      } else if (this.screenType === DEVICE_CHANNELS.DESKTOP) {
        // If screen type is desktop then hide component until width has met breakpoint or greater
        this.renderer.addClass(ele, `hmf-display-none`);
        this.renderer.addClass(ele, `hmf-display-${this.breakPoint}-block`);
      }

      const displayValue = getComputedStyle(ele).getPropertyValue('display');

      if (displayValue && displayValue === 'none') {
        this.prevViewRef = this.viewRef.detach();
      } else {
        this.cdRef.detectChanges();
      }

      if (this.environment.production !== undefined && !this.environment.production) {
        // Resize listener used for breakpoint testing to determine component visibility
        // Only use in non-prod to avoid reduced performance
        this.renderer.listen('window', 'resize', this.debounce(this.displayCheck.bind(this, ele)));
      }
    }
  }

  /* istanbul ignore next */
  // Only used for non-prod testing
  private displayCheck(eleRef: any) {
    let displayValue = getComputedStyle(eleRef).getPropertyValue('display');
    if (!displayValue) {
      this.viewRef.insert(this.prevViewRef);
      displayValue = getComputedStyle(eleRef).getPropertyValue('display');
      if (displayValue === 'none') {
        this.prevViewRef = this.viewRef.detach();
      }
    }
  }

  /* istanbul ignore next */
  // Only used for non-prod testing
  // Debouncing resize listener
  private debounce(func: () => void, timeInMs = 300) {
    let timer;
    return (event) => {
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(func, timeInMs, event);
    };
  }
}
